// InteractorsModal — the "eye-icon" popover that lists every user who
// interacted with a post/reel/story/ad. Tabs across Likes · Views · Shares
// (+ Reposts & Favourites when non-zero). Each row is a tappable micro
// profile chip that navigates to that user's public profile.
//
// Views are only visible to the post owner + their followers — the backend
// enforces that with a 403 which we surface here as a friendly notice.
import { useEffect, useState } from "react";
import { X, Heart, Eye, Share2, Repeat2, Bookmark } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";

const TABS = [
  { key: "like",      label: "Likes",      Icon: Heart },
  { key: "view",      label: "Views",      Icon: Eye },
  { key: "share",     label: "Shares",     Icon: Share2 },
  { key: "repost",    label: "Reposts",    Icon: Repeat2 },
  { key: "favourite", label: "Favourites", Icon: Bookmark },
];

export default function InteractorsModal({ postId, initialKind = "like", counts = {}, onClose, fetchUrl, title }) {
  const [kind, setKind] = useState(initialKind);
  const [state, setState] = useState({ loading: false, users: [], error: null });
  // When `fetchUrl` is provided (e.g. story viewer list), we hit that single URL
  // once and hide the tab strip — no per-kind switching. This is how the story
  // viewer reuses the same modal without needing a `story` tab in the
  // /posts/{id}/interactors endpoint. Feb 2026.
  const singleShot = Boolean(fetchUrl);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setState({ loading: true, users: [], error: null });
      try {
        const url = fetchUrl || `/posts/${postId}/interactors?kind=${kind}&limit=200`;
        const { data } = await api.get(url);
        if (!cancel) setState({ loading: false, users: data.users || [], error: null });
      } catch (e) {
        if (!cancel) {
          const detail = e?.response?.data?.detail;
          setState({ loading: false, users: [], error: e?.response?.status === 403
            ? (fetchUrl ? "Only the story author can see viewers." : "Only the poster and their followers can see viewers.")
            : (detail || "Could not load interactions.") });
        }
      }
    })();
    return () => { cancel = true; };
  }, [postId, kind, fetchUrl]);

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="interactors-modal">
      <div
        className="w-full sm:max-w-md bg-white text-nexus-obsidian rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
          <div className="text-xs tracking-[0.28em] uppercase font-display text-nexus-obsidian">{title || "Who interacted"}</div>
          <button onClick={onClose} className="text-black/50 hover:text-black" data-testid="interactors-close" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        {!singleShot && (
        <div className="flex items-center gap-1 px-2 pt-2 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const c = counts[`${t.key}s`] ?? counts[t.key] ?? 0;
            const active = kind === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setKind(t.key)}
                style={active ? { color: "#fff", backgroundColor: "#0a0a0a" } : undefined}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-display transition ${active ? "" : "text-black/60 hover:bg-black/5"}`}
                data-testid={`interactors-tab-${t.key}`}
              >
                <t.Icon className="w-3.5 h-3.5" style={active ? { color: "#fff" } : undefined} />
                <span style={active ? { color: "#fff" } : undefined}>{t.label}</span>
                <span style={active ? { color: "rgba(255,255,255,0.75)" } : undefined} className={active ? "ml-1 text-[9px]" : "ml-1 text-[9px] text-black/40"}>{c}</span>
              </button>
            );
          })}
        </div>
        )}
        <div className="max-h-[65vh] overflow-y-auto px-2 py-2">
          {state.loading && <div className="p-4 text-xs text-black/50" data-testid="interactors-loading">Loading…</div>}
          {!state.loading && state.error && <div className="p-4 text-xs text-nexus-crimson" data-testid="interactors-error">{state.error}</div>}
          {!state.loading && !state.error && state.users.length === 0 && (
            <div className="p-6 text-center text-xs text-black/50" data-testid="interactors-empty">
              No one has {kind === "like" ? "liked" : kind === "view" ? "viewed" : kind === "share" ? "shared" : kind === "repost" ? "reposted" : "favourited"} this yet.
            </div>
          )}
          <ul className="divide-y divide-black/5">
            {state.users.map((u) => (
              <li key={u.id}>
                <Link
                  to={`/u/${u.id}`}
                  onClick={onClose}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-black/5 transition"
                  data-testid={`interactor-${u.id}`}
                >
                  <img src={u.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${u.id}`} alt="" className="w-10 h-10 rounded-full ring-1 ring-black/10 object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.display_name}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-black/40">{u.role || "member"}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
